import type { Step, CallBackProps, Status, Lifecycle } from 'react-joyride';
import type { ReactNode } from 'react';

export type TourName =
  | 'products'
  | 'wildberries'
  | 'ozon'
  | 'designs'
  | 'categories'
  | 'dashboard'
  | 'sellers'
  | 'users'
  | 'orders'
  | 'settings'
  | 'designer-create'
  | 'designer-stats'
  | 'designer-payouts'
  | 'designer-tariffs'
  | 'layout-jobs'
  | 'layout-create'
  | 'layout-studio'
  | 'layout-job'
  | 'layout-duplicate'
  | 'layout-help';

export type ModalTourName =
  | 'product-create'
  | 'product-edit'
  | 'design-create'
  | 'design-edit'
  | 'design-import'
  | 'designer-theme-create'
  | 'designer-theme-new'
  | 'designer-theme-task'
  | 'designer-theme-upload'
  | 'designer-pdf-upload';

export type WorkflowTourName = 'full-cycle' | 'designer-cabinet';
export type AnyTourName = TourName | ModalTourName | WorkflowTourName;

export const PAGE_TO_TOUR: Record<string, TourName> = {
  '/': 'dashboard',
  '/products': 'products',
  '/wildberries': 'wildberries',
  '/ozon': 'ozon',
  '/designs': 'designs',
  '/categories': 'categories',
  '/sellers': 'sellers',
  '/users': 'users',
  '/orders': 'orders',
  '/settings': 'settings',
  '/designer/create': 'designer-create',
  '/designer/stats': 'designer-stats',
  '/designer/payouts': 'designer-payouts',
  '/designer/tariffs': 'designer-tariffs',
  '/layout-generator': 'layout-jobs',
  '/layout-generator/new': 'layout-create',
  '/layout-generator/help': 'layout-help',
  '/layout-generator/duplicate': 'layout-duplicate',
};

/** Страницы с параметром в пути: точного ключа в PAGE_TO_TOUR у них нет. */
const PAGE_TOUR_PATTERNS: ReadonlyArray<readonly [RegExp, TourName]> = [
  [/^\/layout-generator\/studio\/[^/]+$/, 'layout-studio'],
  [/^\/layout-generator\/jobs\/[^/]+$/, 'layout-job'],
  [/^\/layout-generator\/duplicate\/[^/]+$/, 'layout-duplicate'],
];

export function normalizeTourPath(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] || '/';
  const withSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return withSlash === '/' ? '/' : withSlash.replace(/\/+$/, '');
}

export function resolveTourForPath(path: string): TourName | null {
  const normalized = normalizeTourPath(path);
  const exact = PAGE_TO_TOUR[normalized];
  if (exact) return exact;
  return PAGE_TOUR_PATTERNS.find(([pattern]) => pattern.test(normalized))?.[1] ?? null;
}

export interface TourStep extends Omit<Step, 'target'> {
  target: string;
  title?: string;
  content: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto' | 'center';
  disableBeacon?: boolean;
  disableOverlay?: boolean;
  spotlightClicks?: boolean;
  spotlightPadding?: number;
  hideCloseButton?: boolean;
  hideFooter?: boolean;
  showSkipButton?: boolean;
  showProgress?: boolean;
  navigateTo?: string;
}

export interface TourConfig {
  name: TourName;
  steps: TourStep[];
  continuous?: boolean;
  scrollToFirstStep?: boolean;
  showProgress?: boolean;
  showSkipButton?: boolean;
  disableCloseOnEsc?: boolean;
  disableOverlayClose?: boolean;
}

export interface TourProgress {
  tourName: AnyTourName;
  completed: boolean;
  lastStep: number;
  completedAt?: string;
  skippedAt?: string;
}

export interface TourState {
  activeTour: AnyTourName | null;
  currentStepIndex: number;
  isRunning: boolean;
  tourProgress: Partial<Record<AnyTourName, TourProgress>>;
  startTour: (tourName: TourName) => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (stepIndex: number) => void;
  skipTour: () => void;
  resetTour: (tourName: AnyTourName) => void;
  resetAllTours: () => void;
  handleJoyrideCallback: (data: CallBackProps) => void;
  startModalTour: (modalName: ModalTourName) => void;
  shouldShowModalTour: (modalName: ModalTourName) => boolean;
  startWorkflowTour: (workflowName: WorkflowTourName) => void;
  isWorkflowTourCompleted: (workflowName: WorkflowTourName) => boolean;
  isTourCompleted: (tourName: AnyTourName) => boolean;
  hasTourForPage: (path: string) => boolean;
  getTourForPage: (path: string) => TourName | null;
}

export const TOUR_TITLES: Record<TourName, string> = {
  products: 'Товары',
  wildberries: 'Интеграция Wildberries',
  ozon: 'Интеграция Ozon',
  designs: 'Дизайн макетов',
  categories: 'Категории',
  dashboard: 'Дашборд',
  sellers: 'Продавцы',
  users: 'Пользователи',
  orders: 'Заказы',
  settings: 'Настройки',
  'designer-create': 'Создать макеты',
  'designer-stats': 'Статистика дизайнера',
  'designer-payouts': 'Выплаты дизайнеру',
  'designer-tariffs': 'Тарифы дизайнера',
  'layout-jobs': 'Генератор макетов: список заданий',
  'layout-create': 'Генератор макетов: новое задание',
  'layout-studio': 'Генератор макетов: студия',
  'layout-job': 'Генератор макетов: паспорт серии',
  'layout-duplicate': 'Генератор макетов: дублирование',
  'layout-help': 'Справка генератора макетов',
};

export const MODAL_TOUR_TITLES: Record<ModalTourName, string> = {
  'product-create': 'Создание товара',
  'product-edit': 'Редактирование товара',
  'design-create': 'Создание дизайна',
  'design-edit': 'Редактирование дизайна',
  'design-import': 'Импорт дизайнов',
  'designer-theme-create': 'Создание задания',
  'designer-theme-new': 'Новое задание',
  'designer-theme-task': 'Работа над заданием',
  'designer-theme-upload': 'Загрузка макетов',
  'designer-pdf-upload': 'Загрузка PDF',
};

export const TOUR_DESCRIPTIONS: Record<TourName, string> = {
  products: 'Научитесь управлять товарами: поиск, фильтрация, редактирование и экспорт',
  wildberries: 'Узнайте как синхронизировать товары с маркетплейсом Wildberries',
  ozon: 'Узнайте как синхронизировать товары с маркетплейсом Ozon',
  designs: 'Научитесь работать с дизайн-макетами: загрузка, редактирование, связывание с товарами',
  categories: 'Узнайте как создавать и настраивать категории товаров',
  dashboard: 'Обзор главной страницы и ключевых показателей',
  sellers: 'Управление продавцами и их настройками',
  users: 'Управление пользователями системы',
  orders: 'Работа с заказами',
  settings: 'Настройки системы',
  'designer-create': 'Задания и загрузка готовых макетов',
  'designer-stats': 'Нагрузка, заработок и статистика выполненных макетов',
  'designer-payouts': 'Текущий баланс и история выплат',
  'designer-tariffs': 'Условия оплаты по типам и сложности макетов',
  'layout-jobs': 'Вкладки, фильтры, колонки таблицы и куда ведёт строка задания',
  'layout-create': 'Способы создания, три шага мастера, настройки запуска и лимит расхода',
  'layout-studio': 'Этапы студии, решения оператора, правки и утверждение серии',
  'layout-job': 'Путь серии, сообщения, листы, исправления и отправка на приёмку',
  'layout-duplicate': 'Исходники, формат печати, степень изменения и результаты',
  'layout-help': 'Поиск ответа, курс оператора и проверка себя',
};

export const MODAL_TOUR_DESCRIPTIONS: Record<ModalTourName, string> = {
  'product-create': 'Узнайте, как правильно заполнить форму создания товара',
  'product-edit': 'Научитесь редактировать данные товара и исправлять ошибки',
  'design-create': 'Узнайте, как создать новый дизайн-макет',
  'design-edit': 'Научитесь редактировать метаданные дизайна',
  'design-import': 'Узнайте, как массово импортировать дизайны',
  'designer-theme-create': 'Заполните параметры своего задания',
  'designer-theme-new': 'Возьмите новое задание в работу',
  'designer-theme-task': 'Разберитесь со статусом и требованиями задания',
  'designer-theme-upload': 'Подготовьте и загрузите файлы макетов',
  'designer-pdf-upload': 'Проверьте PDF-файлы перед отправкой',
};

export const WORKFLOW_TOUR_TITLES: Record<WorkflowTourName, string> = {
  'full-cycle': 'Полный цикл: от дизайна до WB',
  'designer-cabinet': 'ЛК Дизайнера: полный обзор',
};

export const WORKFLOW_TOUR_DESCRIPTIONS: Record<WorkflowTourName, string> = {
  'full-cycle': 'Пройдите весь путь от создания дизайна до выгрузки товара на Wildberries',
  'designer-cabinet': 'Все четыре раздела кабинета: задания, загрузка макетов, статистика и деньги',
};

export const AVAILABLE_WORKFLOW_TOURS: WorkflowTourName[] = ['full-cycle', 'designer-cabinet'];

export type { CallBackProps, Status, Lifecycle };
